// src/pages/services/SkillsAssessment.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import VisaDetailLayout from "../../components/VisaDetailLayout";
import {
  FaCheckCircle,
  FaUniversity,
  FaClipboardList,
  FaFileAlt,
  FaLanguage,
  FaUserTie,
  FaLaptopCode,
  FaBriefcase,
  FaCogs,
  FaTools,
  FaQuestionCircle,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  }),
};

const BODIES = [
  { code: "ACS", name: "ACS", desc: "Australian Computer Society (IT professions)" },
  { code: "VETASSESS", name: "VETASSESS", desc: "Wide range of general professional occupations" },
  { code: "CPA", name: "CPA / CA ANZ / IPA", desc: "Accounting bodies (accountants & auditors)" },
  { code: "EA", name: "Engineers Australia", desc: "Professional engineers and technologists" },
  { code: "TRA", name: "TRA", desc: "Trades Recognition Australia (trade occupations)" },
];

const DOCS = [
  { icon: <FaUniversity />, title: "Academic Qualifications", text: "Degree certificates, transcripts, course outlines (if required)." },
  { icon: <FaBriefcase />, title: "Employment Proof", text: "Reference letters, contracts, pay evidence, detailed duties." },
  { icon: <FaLanguage />, title: "English Evidence", text: "IELTS/PTE/TOEFL per assessing body (if applicable)." },
  { icon: <FaFileAlt />, title: "Identity & Supporting", text: "Passport, CV, award letters, statutory declarations." },
];

const STREAMS = {
  IT: {
    icon: <FaLaptopCode />,
    notes: [
      "ACS focuses on qualification relevance to ANZSCO code + skilled employment.",
      "RPL pathway available if degree is not closely related.",
      "Employment letters must include responsibilities matching the code.",
    ],
  },
  Accounting: {
    icon: <FaUserTie />,
    notes: [
      "CPA/CA ANZ/IPA require accredited qualifications & core knowledge areas.",
      "English test often mandatory unless study was in English (per rules).",
      "Some roles need migration skills assessment separate from membership.",
    ],
  },
  Engineering: {
    icon: <FaCogs />,
    notes: [
      "Engineers Australia uses accreditation or competency demonstration (CDR).",
      "Project reports must be authentic and discipline-aligned.",
      "Work experience can support but isn’t always mandatory.",
    ],
  },
  Trades: {
    icon: <FaTools />,
    notes: [
      "TRA pathways vary (JRE, OSAP, Migration Skills Assessment).",
      "Evidence of practical skills + employment essential.",
      "Some pathways require technical interview or workplace assessment.",
    ],
  },
};

export default function SkillsAssessment() {
  const [stream, setStream] = useState("IT");
  const [hasDegree, setHasDegree] = useState(false);
  const [hasExperience, setHasExperience] = useState(false);
  const [hasEnglish, setHasEnglish] = useState(false);

  const readyMsg = useMemo(() => {
    const ok = hasDegree && hasExperience; // English can be arranged later in many bodies
    return ok
      ? "Looks promising! We can shortlist the right assessing body and start your file."
      : "No worries—share your study & work details and we’ll map the best pathway.";
  }, [hasDegree, hasExperience]);

  return (
    <VisaDetailLayout title="Skills Assessment">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-20 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
      </div>

      {/* Intro */}
      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-gray-700 text-lg mb-6"
      >
        Skills assessment verifies your qualifications and work experience against Australian
        standards for skilled migration and related visa pathways. We help you pick the correct
        assessing body, assemble documents, and avoid common pitfalls.
      </motion.p>

      <motion.div
        className="mx-auto mb-6 h-1 w-24 rounded-full bg-gradient-to-r from-blue-600 to-indigo-500"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{ originX: 0 }}
      />

      {/* Assessing Bodies */}
      <section className="mt-6">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-900 font-semibold mb-3"
        >
          Common Assessing Bodies
        </motion.h3>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {BODIES.map((b, i) => (
            <motion.div
              key={b.code}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
            >
              <Tilt glareEnable glareMaxOpacity={0.15} glareColor="#60a5fa" glarePosition="all">
                <div className="h-full rounded-xl border border-white/60 bg-white/70 p-4 shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)]">
                  <div className="text-lg font-semibold text-blue-900">{b.name}</div>
                  <div className="mt-1 text-xs text-gray-600">{b.desc}</div>
                </div>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Streams (tabs) */}
      <section className="mt-8">
        <div className="flex flex-wrap items-center gap-3">
          {Object.keys(STREAMS).map((k) => (
            <button
              key={k}
              onClick={() => setStream(k)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                stream === k
                  ? "bg-blue-600 text-white shadow"
                  : "bg-white text-blue-700 border hover:bg-blue-50"
              }`}
            >
              {STREAMS[k].icon} {k}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={stream}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
            className="mt-4 rounded-2xl border bg-gradient-to-b from-blue-50/70 to-white p-6"
          >
            <h4 className="text-blue-900 font-semibold mb-2">{stream} — Key Points</h4>
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
              {STREAMS[stream].notes.map((n) => (
                <li key={n} className="flex items-start gap-2">
                  <FaCheckCircle className="mt-1 text-emerald-500" /> {n}
                </li>
              ))}
            </ul>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* Document checklist */}
      <section className="mt-8">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-900 font-semibold mb-3"
        >
          What You Need
        </motion.h3>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {DOCS.map((d, i) => (
            <motion.div
              key={d.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
            >
              <Tilt glareEnable glareMaxOpacity={0.15} glareColor="#60a5fa" glarePosition="all">
                <div className="h-full rounded-2xl border border-white/60 bg-white/70 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.06)] backdrop-blur hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)] transition">
                  <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-200">
                    {d.icon}
                  </div>
                  <h4 className="text-blue-900 font-semibold">{d.title}</h4>
                  <p className="mt-1 text-gray-700 text-sm leading-relaxed">{d.text}</p>
                </div>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Readiness checker */}
      <section className="mt-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/80 p-6 shadow-sm backdrop-blur"
        >
          <div className="text-blue-800 font-semibold mb-3 flex items-center gap-2">
            <FaClipboardList /> Quick Readiness Check
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={hasDegree} onChange={(e) => setHasDegree(e.target.checked)} />
              I have my degree certificates/transcripts ready
            </label>
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={hasExperience} onChange={(e) => setHasExperience(e.target.checked)} />
              I can provide detailed work references & pay evidence
            </label>
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={hasEnglish} onChange={(e) => setHasEnglish(e.target.checked)} />
              I meet/plan to meet the English requirement
            </label>
          </div>

          <div className={`mt-4 rounded-lg px-4 py-3 text-sm ${hasDegree && hasExperience ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"}`}>
            {readyMsg}
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-900 font-semibold mb-3"
        >
          FAQs
        </motion.h3>

        <div className="space-y-3">
          {[
            {
              q: "How long does a skills assessment take?",
              a: "Anywhere from a few weeks to a few months depending on the assessing body and whether additional information is requested.",
            },
            {
              q: "Do I need English test results first?",
              a: "Some bodies require English at lodgement, others allow it later. We’ll confirm the requirement for your occupation/body.",
            },
            {
              q: "Can work experience compensate for a non-closely related degree?",
              a: "Often yes (e.g., ACS RPL pathway). It depends on the body and ANZSCO code. We’ll map the best option for you.",
            },
          ].map((item, i) => (
            <Accordion key={i} title={item.q} body={item.a} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
        >
          <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
            <div className="grid items-center gap-6 md:grid-cols-2">
              <div>
                <h4 className="text-2xl font-bold text-blue-900">
                  Ready to start your skills assessment?
                </h4>
                <p className="mt-1 text-gray-600">
                  We’ll identify the right assessing body, prepare documents, and manage submission.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500"
                >
                  Talk to a  Consultant
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </VisaDetailLayout>
  );
}

/* Small accordion */
function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between text-left">
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-2 text-gray-700 text-sm leading-relaxed"
          >
            {body}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
